// packages/web/src/lib/householdTabs.ts — A3: WHAT EACH OF THE PACK'S TABS
// SHOWS, as data.
//
// The rail itself is the pack's (actor-assist/src/index.ts declares nine tabs
// in three groups, and the A2 spec is explicit that the shell must not
// re-decide it). What the pack does NOT declare is which record types sit
// behind each of its tab keys — tab keys are opaque to Core by design — so
// that mapping lives here, in one table, rather than as nine screens each
// holding its own opinion.
//
// KEPT OUT OF App.tsx DELIBERATELY. App.tsx is a shared file under the lane
// collision protocol, where every edit is a merge risk paid by both lanes;
// a table that will grow with the pack does not belong in it. App.tsx reads
// one function from here and renders it.
import type { RegistrySection } from '../screens/Registry';

/** The pack's own labels, from recordTypes.ts's label_plural. Copied here
 * as display text only — nothing routes or gates on these strings. */
export const HOUSEHOLD_TABS: Record<string, { eyebrow: string; sections: RegistrySection[] }> = {
  // A4 — `auditions` and `projects` USED TO BE DECLARED HERE and are not
  // any more. They have real screens now (HouseholdAuditions, HouseholdProjects,
  // ProjectSpine, RoleSurface, AuditionSpread), and App.tsx routes to those
  // before it reaches this table.
  //
  // The declarations are DELETED rather than left in place, because a table
  // entry nothing reads is a claim about the app that is no longer true — the
  // next person to read this file would believe Auditions is a registry and
  // go looking for it in Registry.tsx. `aa_role` and `aa_sides` are no longer
  // listed anywhere here either: they are reached through the part they
  // belong to, which is the only place either one means anything.
  // W2 (`9753e5ed`) — `people` IS GONE FROM HERE TOO, for the same reason and
  // by the same rule as `auditions` and `projects` above: it has a real screen
  // now (HouseholdPeople + HouseholdPerson), App.tsx routes to it before it
  // reaches this table, and a declaration nothing reads is a claim about the
  // app that has stopped being true. Deleted rather than left in place.
  //
  // ⚠ AND DELETING IT MATTERS MORE HERE THAN IT DID THERE. `householdTab` is
  // a static map with no notion of vertical, and this entry was what handed a
  // PERSONAL build the actor pack's People tab — a registry over `aa_*` types
  // that pack has never heard of — for the whole window before the duplicate
  // `case 'people':` was found (App.tsx's own note on it). With the entry gone
  // that particular wrong answer has nowhere left to come from.
  // ⚑ P6 — `money` IS NOT A REGISTRY TAB, AND ITS ENTRY IS STILL READ.
  //
  // App.tsx routes `money` to HouseholdMoney (the chase, the set-aside, then
  // the rows) rather than through `householdRegistry`, so nothing here draws
  // this screen. The entry is NOT deleted the way `auditions` and `projects`
  // were, because it is not a stale claim: HouseholdMoney reads these two
  // sections for the LABELS AND EMPTY LINES of its own two row lists. It had
  // its own copy of both strings, which is precisely the second opinion this
  // table exists to prevent.
  //
  // ⚠ AND THOSE TWO LISTS RENDER ON EVERY TIER. The money SCREEN's accounting
  // is Pro's (`accounting.contracted_income`), and below Pro the tab is not
  // offered at all — but the payments and expenses themselves are the
  // household's own records and stay listed, with a named line saying what is
  // withheld. A plan may withhold a derivation; it may not hold entries
  // hostage (framework §5).
  money: {
    eyebrow: 'Money',
    sections: [
      { typeKey: 'aa_payment', label: 'Payments', empty: 'Nothing paid or owing yet.' },
      { typeKey: 'aa_expense', label: 'Expenses', empty: 'No expenses recorded yet.' },
    ],
  },
  documents: {
    eyebrow: 'Documents',
    sections: [{ typeKey: 'aa_document', label: 'Documents', empty: 'No paperwork on file yet.' }],
  },
  materials: {
    eyebrow: 'Materials',
    sections: [
      { typeKey: 'aa_media', label: 'Media', empty: 'No headshots, reels or tapes yet.' },
      { typeKey: 'aa_profile', label: 'Profiles', empty: 'No casting profiles yet.' },
    ],
  },
  development: {
    eyebrow: 'Development',
    sections: [
      { typeKey: 'aa_training', label: 'Training', empty: 'No classes or coaching recorded yet.' },
      { typeKey: 'aa_award', label: 'Awards', empty: 'No awards recorded yet.' },
      { typeKey: 'aa_skill', label: 'Skills', empty: 'No skills recorded yet.' },
    ],
  },
};

/** Null for a tab this table does not describe — `today` (its own screen)
 * and `stats` (a derived surface, not a list). A caller that gets null must
 * render something else, never an empty registry. */
export function householdTab(tab: string): { eyebrow: string; sections: RegistrySection[] } | null {
  return HOUSEHOLD_TABS[tab] ?? null;
}
